// Platform predicates for picking an upload UX.
//
// The only consumer today is useFilePicker: Android Chrome needs Hestia's own
// Camera/Photos/Files sheet, iOS Safari does not (it already shows a good
// native one). Keep these pure and UA-based so they stay unit-testable.

/**
 * True on iPhone/iPad/iPod.
 *
 * iPadOS 13+ reports a desktop Safari UA ("Macintosh; Intel Mac OS X"), so the
 * UA regex alone misses iPads. `maxTouchPoints > 1` is the standard tiebreaker:
 * a real Mac reports 0 (or 1 with a touchscreen-emulating peripheral), an iPad
 * reports 5.
 */
export const isIOS = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/i.test(ua)) return true;
    return /Macintosh|Mac OS X/i.test(ua) && (navigator.maxTouchPoints || 0) > 1;
};

/**
 * True when the device has a touch-primary pointer (phone/tablet) rather than
 * a mouse. `(pointer: coarse)` is the CSS-level signal and is more reliable
 * than sniffing for "Mobi" in the UA.
 */
export const isCoarsePointer = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }
    return window.matchMedia('(pointer: coarse)').matches;
};

/**
 * Whether to show Hestia's in-app file-source sheet instead of going straight
 * to a single hidden <input type="file">.
 *
 * Android only, deliberately:
 *  - Android Chrome builds its intent chooser from the apps that can handle the
 *    declared MIME types, so `accept="image/*,application/pdf"` silently drops
 *    Google Photos and leaves the user with Camera + Files only. Our sheet
 *    routes "Photos" to a narrowed `image/*` input, which puts the gallery back.
 *  - iOS Safari already shows Take Photo / Photo Library / Choose File for a
 *    bare `accept="image/*"`. Our sheet on top of that would mean two sheets
 *    for one pick, so iOS keeps the native path.
 *  - Desktop has no camera intent and a perfectly good file dialog.
 */
export const shouldUseFileSourceChooser = () => isCoarsePointer() && !isIOS();
